import { FileUploadData, FileUploadResponse, FileView } from "@/types/file";
import { apiUrl } from "../../utils/url";
import { toServiceError } from "../errors";
import { FILE_TRANSFER_TIMEOUT_MS, HttpClient } from "../HttpClient";
import { IFilesService } from "./IFilesService";

export class FilesService implements IFilesService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {},
    });
  }

  async uploadFile(fileData: FileUploadData, typeId: number): Promise<number> {
    const uploaded = await this.uploadImage(fileData.file, typeId);
    return uploaded.id;
  }

  /**
   * Upload a file and return the stored record.
   *
   * The endpoint is `POST /api/files?typeId=`. This used to post to
   * `/files/upload?typeId=`, which resolves to `/api/files/{id}` with `id` of
   * "upload" and answers a 500 type-mismatch — it had no callers, so nothing
   * depended on the broken path.
   *
   * The response carries the record as JSON, but `idFromLocation` is still
   * passed: when a `Location` header is present `HttpClient` short-circuits and
   * returns the id alone, so both shapes are normalised back to a `FileView`
   * here rather than at every call site.
   */
  async uploadImage(
    file: File,
    typeId: number,
    description?: string,
  ): Promise<FileView> {
    const formData = new FormData();
    formData.append("file", file);

    /*
     * Never upload without a description. It is the image's alt text in the
     * client app, and a null one breaks more than accessibility: the app
     * validates a news article's file with `description: z.string()`, which
     * accepts undefined but rejects null, and one rejected article fails the
     * whole news list. Falling back to the file's own name keeps the value a
     * string when the caller has nothing better.
     */
    const query = new URLSearchParams({ typeId: String(typeId) });
    query.set("description", description?.trim() || file.name);

    let response: FileView | number;
    try {
      response = await this.httpClient.post<FileView | number>(
        `/files?${query.toString()}`,
        formData,
        {
          timeout: FILE_TRANSFER_TIMEOUT_MS,
          headers: {
            Accept: "application/json",
          },
        },
        true,
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar o ficheiro. Confirme o formato, o tamanho e o nome do ficheiro.",
      );
    }

    if (typeof response === "number") {
      if (!Number.isFinite(response)) {
        throw new Error(
          "O ficheiro foi carregado mas o servidor não devolveu o identificador.",
        );
      }
      return {
        id: response,
        name: file.name,
        description: description?.trim() || file.name,
      };
    }

    if (response?.id == null) {
      throw new Error(
        "O ficheiro foi carregado mas o servidor não devolveu o identificador.",
      );
    }

    return response;
  }

  async downloadContactFile(): Promise<Blob> {
    try {
      return await this.httpClient.get<Blob>(`/files/download/contactFile`, {
        timeout: FILE_TRANSFER_TIMEOUT_MS,
        headers: {
          Accept: "application/octet-stream",
        },
      });
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível descarregar o modelo de contactos. Tente novamente mais tarde.",
      );
    }
  }

  async uploadContactFile(
    fileData: FileUploadData,
  ): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append("file", fileData.file);

      return await this.httpClient.post<FileUploadResponse>(
        `/files/upload/contactFile`,
        formData,
        {
          timeout: FILE_TRANSFER_TIMEOUT_MS,
          headers: {
            Accept: "application/json",
          },
        },
      );
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível importar o ficheiro de contactos. Use o modelo descarregado em formato .xlsx.",
      );
    }
  }

  async downloadSaleCheckoutFile(saleId: number): Promise<Blob> {
    try {
      const response = await this.httpClient.get<Blob>(
        `/files/sale/${saleId}/pdf`,
        {
          timeout: FILE_TRANSFER_TIMEOUT_MS,
          headers: {
            Accept: "*/*",
          },
        },
      );

      return response;
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível descarregar o resumo da venda. Confirme se a venda está carregada e tente novamente.",
      );
    }
  }
}
